/**
 * Document Tracking API - Track when proposals/documents are viewed
 */
import apiClient from './index'

export interface DocumentView {
  id: string
  document_type: string
  document_id: string
  viewer_email?: string
  viewer_name?: string
  viewed_at: string
  time_on_page_seconds: number
  scroll_depth_percent: number
  pages_viewed: number[]
  device_type?: string
  browser?: string
  location_city?: string
  location_country?: string
  is_active: boolean
}

export interface DocumentViewStats {
  total_views: number
  unique_viewers: number
  total_time_seconds: number
  avg_time_seconds: number
  avg_scroll_depth: number
  first_viewed_at?: string
  last_viewed_at?: string
}

export interface TrackableDocument {
  id: string
  document_type: string
  document_id: string
  tracking_token: string
  short_code: string
  require_email: boolean
  password_protected: boolean
  expires_at?: string
  max_views?: number
  notify_on_view: boolean
  view_count: number
  first_viewed_at?: string
  last_viewed_at?: string
  created_at: string
  stats?: DocumentViewStats
}

export interface CreateTrackableDocumentData {
  document_type: string
  document_id: string
  require_email?: boolean
  password_protected?: boolean
  password?: string
  expires_at?: string
  max_views?: number
  notify_on_view?: boolean
  notify_email?: string
}

export interface TrackableDocumentResponse {
  data: TrackableDocument
}

export interface TrackableDocumentsResponse {
  data: TrackableDocument[]
}

export interface DocumentViewsResponse {
  data: {
    views: DocumentView[]
    stats: DocumentViewStats
  }
}

export interface RecentViewsResponse {
  data: DocumentView[]
}

// ============== Trackable Document Links ==============

/**
 * Get all trackable document links
 */
export async function getTrackableDocuments(
  documentType?: string
): Promise<TrackableDocument[]> {
  const params = documentType ? `?document_type=${documentType}` : ''
  const response = await apiClient.get<TrackableDocumentsResponse>(
    `/documents/trackable${params}`
  )
  return response.data.data
}

/**
 * Create a trackable link for a document
 */
export async function createTrackableDocument(
  data: CreateTrackableDocumentData
): Promise<TrackableDocument> {
  const response = await apiClient.post<TrackableDocumentResponse>(
    '/documents/trackable',
    data
  )
  return response.data.data
}

/**
 * Get a trackable document with stats
 */
export async function getTrackableDocument(
  docId: string
): Promise<TrackableDocument> {
  const response = await apiClient.get<TrackableDocumentResponse>(
    `/documents/trackable/${docId}`
  )
  return response.data.data
}

/**
 * Delete a trackable document link
 */
export async function deleteTrackableDocument(docId: string): Promise<void> {
  await apiClient.delete(`/documents/trackable/${docId}`)
}

// ============== Document Views ==============

/**
 * Get views for a specific document
 */
export async function getDocumentViews(
  documentType: string,
  documentId: string
): Promise<{ views: DocumentView[]; stats: DocumentViewStats }> {
  const response = await apiClient.get<DocumentViewsResponse>(
    `/documents/${documentType}/${documentId}/views`
  )
  return response.data.data
}

/**
 * Get recent document views across all documents
 */
export async function getRecentDocumentViews(
  limit = 50
): Promise<DocumentView[]> {
  const response = await apiClient.get<RecentViewsResponse>(
    `/documents/views/recent?limit=${limit}`
  )
  return response.data.data
}

// ============== Public Tracking (for embedding in public pages) ==============

/**
 * Get public document data
 */
export async function getPublicDocument(shortCode: string): Promise<{
  document_type: string
  require_email: boolean
  password_protected: boolean
  document: any
}> {
  const response = await apiClient.get(`/d/${shortCode}`)
  return response.data.data
}

/**
 * Verify access to a protected document
 */
export async function verifyDocumentAccess(
  shortCode: string,
  data: { password?: string; email?: string }
): Promise<{ session_id: string; verified: boolean }> {
  const response = await apiClient.post(`/d/${shortCode}/verify`, data)
  return response.data.data
}

/**
 * Record a document view (call when document is opened)
 */
export async function recordDocumentView(
  shortCode: string,
  data: {
    email?: string
    name?: string
    session_id?: string
    page_count?: number
  }
): Promise<{ view_id: string; session_id: string }> {
  const response = await apiClient.post(`/d/${shortCode}/view`, data)
  return response.data.data
}

/**
 * Send heartbeat to update engagement metrics
 */
export async function sendViewHeartbeat(
  shortCode: string,
  data: {
    session_id: string
    time_on_page_seconds: number
    scroll_depth_percent: number
    pages_viewed?: number[]
  }
): Promise<void> {
  await apiClient.post(`/d/${shortCode}/heartbeat`, data)
}

/**
 * End a viewing session
 */
export async function endDocumentView(
  shortCode: string,
  data: {
    session_id: string
    time_on_page_seconds: number
    scroll_depth_percent: number
    pages_viewed?: number[]
  }
): Promise<void> {
  await apiClient.post(`/d/${shortCode}/end`, data)
}

/**
 * Publicly accept + e-sign a shared proposal.
 */
export async function acceptPublicDocument(
  shortCode: string,
  data: { name: string; title?: string; agree: boolean; signature_data?: string }
): Promise<{ signed?: boolean; already_signed?: boolean; signed_name?: string; signed_at?: string }> {
  const res = await apiClient.post(`/d/${shortCode}/accept`, data)
  return res.data.data
}

// ============== Helpers ==============

/**
 * Generate a shareable tracking URL
 */
export function getTrackingUrl(shortCode: string): string {
  const baseUrl = window.location.origin
  return `${baseUrl}/view/${shortCode}`
}

/**
 * Format time on page for display
 */
export function formatTimeOnPage(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`
  }
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

/**
 * Get device icon name based on device type
 */
export function getDeviceIcon(deviceType?: string): string {
  switch (deviceType) {
    case 'mobile':
      return 'smartphone'
    case 'tablet':
      return 'tablet'
    case 'desktop':
    default:
      return 'monitor'
  }
}
